import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  bookingsApi,
  BookingsListParams,
  CreateBookingData,
  UpdateBookingData,
  BookingStatus,
} from '../services/bookings';

// Query keys
export const bookingKeys = {
  all: ['bookings'] as const,
  lists: () => [...bookingKeys.all, 'list'] as const,
  list: (params: BookingsListParams) => [...bookingKeys.lists(), params] as const,
  details: () => [...bookingKeys.all, 'detail'] as const,
  detail: (id: string) => [...bookingKeys.details(), id] as const,
  calendar: (start: string, end: string) => [...bookingKeys.all, 'calendar', start, end] as const,
  stats: () => [...bookingKeys.all, 'stats'] as const,
  availability: (serviceId: string, date: string) => [...bookingKeys.all, 'availability', serviceId, date] as const,
};

// List bookings
export function useBookings(params: BookingsListParams = {}) {
  return useQuery({
    queryKey: bookingKeys.list(params),
    queryFn: () => bookingsApi.list(params),
  });
}

// Get single booking
export function useBooking(id: string) {
  return useQuery({
    queryKey: bookingKeys.detail(id),
    queryFn: () => bookingsApi.get(id),
    enabled: !!id,
  });
}

// Get calendar events
export function useCalendarEvents(start: string, end: string) {
  return useQuery({
    queryKey: bookingKeys.calendar(start, end),
    queryFn: () => bookingsApi.getCalendarEvents(start, end),
    enabled: !!start && !!end,
  });
}

// Get booking stats
export function useBookingStats() {
  return useQuery({
    queryKey: bookingKeys.stats(),
    queryFn: () => bookingsApi.getStats(),
  });
}

// Get availability
export function useAvailability(serviceId: string, date: string) {
  return useQuery({
    queryKey: bookingKeys.availability(serviceId, date),
    queryFn: () => bookingsApi.getAvailability(serviceId, date),
    enabled: !!serviceId && !!date,
  });
}

// Create booking
export function useCreateBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateBookingData) => bookingsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.stats() });
      queryClient.invalidateQueries({ queryKey: ['bookings', 'calendar'] });
    },
  });
}

// Update booking
export function useUpdateBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBookingData }) =>
      bookingsApi.update(id, data),
    onSuccess: (booking) => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.setQueryData(bookingKeys.detail(booking._id), booking);
      queryClient.invalidateQueries({ queryKey: ['bookings', 'calendar'] });
    },
  });
}

// Update status
export function useUpdateBookingStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      status,
      cancellationReason,
      cancellationNote,
    }: {
      id: string;
      status: BookingStatus;
      cancellationReason?: string;
      cancellationNote?: string;
    }) => bookingsApi.updateStatus(id, status, cancellationReason, cancellationNote),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.stats() });
      queryClient.invalidateQueries({ queryKey: ['bookings', 'calendar'] });
    },
  });
}

// Reschedule booking
export function useRescheduleBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, startTime, reason }: { id: string; startTime: string; reason?: string }) =>
      bookingsApi.reschedule(id, startTime, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.stats() });
      queryClient.invalidateQueries({ queryKey: ['bookings', 'calendar'] });
    },
  });
}

// Delete booking
export function useDeleteBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => bookingsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.stats() });
      queryClient.invalidateQueries({ queryKey: ['bookings', 'calendar'] });
    },
  });
}

// Add note
export function useAddBookingNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bookingId, content }: { bookingId: string; content: string }) =>
      bookingsApi.addNote(bookingId, content),
    onSuccess: (_, { bookingId }) => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(bookingId) });
    },
  });
}
